"use client";

import { getLetterStatuses } from "@/lib/daily/scoreCalculator";
import { Delete } from "lucide-react";

interface FinWordKeyboardProps {
  readonly onKeyPress: (key: string) => void;
  readonly guesses: readonly string[];
  readonly solution: string;
  readonly disabled?: boolean;
}

const KEYS_ROW_1 = ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"];
const KEYS_ROW_2 = ["A", "S", "D", "F", "G", "H", "J", "K", "L"];
const KEYS_ROW_3 = ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"];

export default function FinWordKeyboard({
  onKeyPress,
  guesses,
  solution,
  disabled = false,
}: FinWordKeyboardProps) {
  // Aggregate status of guessed letters
  const letterStatuses: Record<string, "correct" | "present" | "absent"> = {};

  guesses.forEach((guess) => {
    const statuses = getLetterStatuses(guess, solution);
    for (let i = 0; i < 5; i++) {
      const char = guess[i];
      const status = statuses[i];

      const currentStatus = letterStatuses[char];
      if (status === "correct") {
        letterStatuses[char] = "correct";
      } else if (status === "present") {
        if (currentStatus !== "correct") {
          letterStatuses[char] = "present";
        }
      } else if (status === "absent") {
        if (currentStatus !== "correct" && currentStatus !== "present") {
          letterStatuses[char] = "absent";
        }
      }
    }
  });

  const renderKey = (key: string) => {
    const isSpecial = key === "ENTER" || key === "BACKSPACE";
    const status = letterStatuses[key];

    let btnClass = "keyboard-key";
    if (isSpecial) btnClass += " wide";
    if (status) btnClass += ` ${status}`;

    const handleKeyClick = () => {
      if (disabled) return;
      onKeyPress(key);
    };

    return (
      <button
        key={key}
        className={btnClass}
        onClick={handleKeyClick}
        disabled={disabled}
        aria-label={key === "BACKSPACE" ? "Backspace" : key}
      >
        {key === "BACKSPACE" ? <Delete size={18} /> : key}
      </button>
    );
  };

  return (
    <div className="finword-keyboard" role="group" aria-label="Keyboard">
      <div className="keyboard-row">{KEYS_ROW_1.map(renderKey)}</div>
      <div className="keyboard-row">{KEYS_ROW_2.map(renderKey)}</div>
      <div className="keyboard-row">{KEYS_ROW_3.map(renderKey)}</div>
    </div>
  );
}
